const ARCHIVED_LIFECYCLES = new Set(['archived', 'deactivated', 'deleted']);
const COMPLETED_STATUSES = new Set(['completed', 'fulfilled', 'resolved', 'closed']);
const EXECUTION_STATUSES = new Set(['running', 'in_progress', 'executing', 'in_execution']);
const ATTENTION_STATUSES = new Set([
  'action_required',
  'attention_required',
  'blocked',
  'failed',
  'rejected',
]);
const WAITING_PROVIDER_STATUSES = new Set([
  'awaiting_provider',
  'pending_provider',
  'provider_action_required',
  'waiting_on_provider',
]);
const WAITING_REQUESTOR_STATUSES = new Set([
  'awaiting_requestor',
  'pending_requestor',
  'requestor_action_required',
  'waiting_on_requestor',
]);

const asObject = (value) => (
  value && typeof value === 'object' && !Array.isArray(value) ? value : {}
);

const firstValue = (...values) => values.find((value) => (
  value !== undefined && value !== null && String(value).trim() !== ''
));

const normalizeKey = (value) => String(value || '').trim().toLowerCase().replace(/[\s-]+/g, '_');

const countValue = (...values) => {
  const value = firstValue(...values);
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? Math.floor(parsed) : 0;
};

const booleanValue = (...values) => values.some((value) => (
  value === true || value === 1 || normalizeKey(value) === 'true'
));

const entityName = (entity) => {
  if (!entity) return '';
  if (typeof entity === 'string') return entity.trim();
  return String(firstValue(
    entity.display_name,
    entity.full_name,
    entity.user_full_name,
    entity.name,
    entity.legal_name,
    entity.email,
  ) || '').trim();
};

const latestDate = (...values) => {
  const validDates = values
    .flat()
    .filter(Boolean)
    .map((value) => ({ value, timestamp: new Date(value).getTime() }))
    .filter(({ timestamp }) => Number.isFinite(timestamp));

  if (!validDates.length) return firstValue(...values.flat()) || null;
  validDates.sort((left, right) => right.timestamp - left.timestamp);
  return validDates[0].value;
};

const getOperationalStatus = ({
  lifecycle,
  requestStatus,
  executionStatus,
  attentionRequired,
  activeRunsCount,
}) => {
  if (ARCHIVED_LIFECYCLES.has(lifecycle)) {
    return { key: 'archived', label: 'Archived', color: 'default' };
  }
  if (attentionRequired || ATTENTION_STATUSES.has(requestStatus)) {
    return { key: 'attention', label: 'Needs Attention', color: 'warning' };
  }
  if (activeRunsCount > 0 || EXECUTION_STATUSES.has(executionStatus) || EXECUTION_STATUSES.has(requestStatus)) {
    return { key: 'execution', label: 'In Execution', color: 'info' };
  }
  if (WAITING_PROVIDER_STATUSES.has(requestStatus)) {
    return { key: 'waiting_provider', label: 'Waiting on Provider', color: 'warning' };
  }
  if (WAITING_REQUESTOR_STATUSES.has(requestStatus)) {
    return { key: 'waiting_requestor', label: 'Waiting on Requestor', color: 'warning' };
  }
  if (COMPLETED_STATUSES.has(requestStatus) || COMPLETED_STATUSES.has(lifecycle)) {
    return { key: 'completed', label: 'Completed', color: 'success' };
  }
  return { key: 'active', label: 'Active', color: 'success' };
};

const participantNames = (workspace, partnerName, creatorName) => {
  const suppliedParticipants = Array.isArray(workspace.participants) ? workspace.participants : [];
  const providers = Array.isArray(workspace.providers)
    ? workspace.providers
    : Array.isArray(workspace.provider_business_units)
      ? workspace.provider_business_units
      : [];
  const names = [
    ...suppliedParticipants.map(entityName),
    entityName(workspace.requestor || workspace.requesting_business_unit),
    ...providers.map(entityName),
    creatorName,
    partnerName,
  ].filter(Boolean);

  return [...new Set(names)];
};

export const normalizeWorkspaceCard = (workspace = {}) => {
  const metadata = asObject(workspace.metadata);
  const stats = {
    ...asObject(metadata.workspace_stats),
    ...asObject(workspace.stats),
  };
  const request = asObject(workspace.request);
  const capabilityRequest = asObject(workspace.capabilityRequest || workspace.capability_request);
  const partner = asObject(
    workspace.partnerScope
    || workspace.partner_scope
    || workspace.businessUnit
    || workspace.business_unit
    || workspace.requesting_business_unit
    || workspace.created_by_partner,
  );
  const creator = asObject(workspace.createdBy || workspace.created_by_user);
  const linkedRequestId = firstValue(
    request.id,
    request.request_id,
    capabilityRequest.request_id,
    workspace.request_id,
    metadata.request_id,
    metadata.request_capability_id,
  );

  const lifecycle = normalizeKey(firstValue(
    workspace.lifecycle,
    workspace.status,
    metadata.lifecycle,
    'active',
  ));
  const requestStatus = normalizeKey(firstValue(
    request.status,
    capabilityRequest.status,
    workspace.requestStatus,
    workspace.request_status,
    metadata.last_updated_request_capability_status,
    metadata.request_capability_status,
    metadata.request_status,
  ));
  const executionStatus = normalizeKey(firstValue(
    workspace.executionStatus,
    workspace.execution_status,
    asObject(workspace.run).status,
    metadata.execution_status,
    stats.execution_status,
  ));
  const messagesCount = countValue(
    workspace.messagesCount,
    workspace.messages_count,
    stats.message_count,
    stats.messages_count,
    metadata.message_count,
    metadata.messages_count,
    metadata.total_messages,
  );
  const threadsCount = countValue(
    workspace.threadsCount,
    workspace.threads_count,
    stats.correlation_count,
    stats.threads_count,
  );
  const artifactsCount = countValue(
    workspace.artifactsCount,
    workspace.artifacts_count,
    stats.artifact_count,
    stats.artifacts_count,
    metadata.artifact_count,
    metadata.artifacts_count,
  );
  const runsCount = countValue(
    workspace.runsCount,
    workspace.runs_count,
    stats.run_count,
    stats.runs_count,
    metadata.run_count,
    metadata.runs_count,
  );
  const activeRunsCount = countValue(
    workspace.activeRunsCount,
    workspace.active_runs_count,
    stats.active_run_count,
    stats.active_runs_count,
    metadata.active_run_count,
    metadata.active_runs_count,
  );
  const tasksCount = countValue(
    workspace.tasksCount,
    workspace.tasks_count,
    stats.task_count,
    stats.tasks_count,
    metadata.task_count,
    metadata.tasks_count,
  );
  const attentionRequired = booleanValue(
    workspace.attentionRequired,
    workspace.attention_required,
    workspace.hasUnreadActivity,
    workspace.has_unread_activity,
    stats.attention_required,
    stats.has_unread_activity,
    metadata.attention_required,
    metadata.has_unread_activity,
  );
  const partnerScopeName = String(firstValue(
    partner.display_name,
    partner.name,
    partner.legal_name,
    workspace.partner_scope_name,
    workspace.business_unit_name,
  ) || '').trim();
  const partnerScopeCode = String(firstValue(
    partner.code,
    partner.partner_code,
    partner.business_unit_code,
    workspace.partner_scope_code,
    workspace.business_unit_code,
  ) || '').trim();
  const createdBy = entityName(creator) || String(firstValue(workspace.created_by_name) || '').trim();
  const updatedAt = latestDate(
    workspace.updatedAt,
    workspace.updated_at,
    workspace.lastActivityAt,
    workspace.last_activity_at,
    stats.latest_activity_at,
    stats.latest_message_created_at,
    asObject(metadata.provider_access).latest_message_created_at,
    asObject(metadata.provider_access).latest_access_updated_at,
    workspace.createdAt,
    workspace.created_at,
  );

  const normalized = {
    id: String(firstValue(workspace.id, workspace.workspace_id) || ''),
    title: String(firstValue(workspace.title, workspace.name, 'Untitled Workspace')),
    description: String(firstValue(workspace.description) || ''),
    lifecycle,
    requestTitle: String(firstValue(
      request.title,
      request.request_title,
      capabilityRequest.request_title,
      capabilityRequest.title,
      workspace.request_title,
      metadata.request_title,
      linkedRequestId ? workspace.title : null,
    ) || ''),
    requestStatus,
    capabilityName: String(firstValue(
      capabilityRequest.capability_name,
      asObject(capabilityRequest.capability).name,
      workspace.capability_name,
      metadata.capability_name,
    ) || ''),
    partnerScopeName,
    partnerScopeCode,
    messagesCount,
    threadsCount,
    artifactsCount,
    runsCount: Math.max(runsCount, activeRunsCount),
    activeRunsCount,
    tasksCount,
    runProgress: countValue(
      workspace.runProgress,
      workspace.run_progress,
      asObject(workspace.run).progress,
      metadata.run_progress,
    ),
    executionStatus,
    updatedAt,
    createdBy,
    participants: participantNames(workspace, partnerScopeName, createdBy),
    attentionRequired: attentionRequired || ATTENTION_STATUSES.has(requestStatus),
    isCui: Boolean(workspace.is_cui || workspace.isCui),
    raw: workspace,
  };

  normalized.status = getOperationalStatus(normalized);
  normalized.isArchived = normalized.status.key === 'archived';
  normalized.isInExecution = normalized.status.key === 'execution';
  normalized.searchText = [
    normalized.id,
    normalized.title,
    normalized.description,
    normalized.requestTitle,
    normalized.capabilityName,
    normalized.partnerScopeName,
    normalized.partnerScopeCode,
    normalized.createdBy,
    normalized.participants.join(' '),
    normalized.status.label,
    normalized.requestStatus,
  ].join(' ').toLowerCase();

  return normalized;
};

export const mergeWorkspaceLists = (...lists) => {
  const merged = new Map();

  lists.flat().filter(Boolean).forEach((workspace) => {
    const id = String(firstValue(workspace.workspace_id, workspace.id) || '');
    if (!id) return;
    const current = merged.get(id);
    if (!current) {
      merged.set(id, workspace);
      return;
    }

    const currentMetadata = asObject(current.metadata);
    const incomingMetadata = asObject(workspace.metadata);
    merged.set(id, {
      ...current,
      ...workspace,
      title: firstValue(current.title, workspace.title),
      description: firstValue(current.description, workspace.description, ''),
      created_by_user: current.created_by_user || workspace.created_by_user,
      created_by_partner: current.created_by_partner || workspace.created_by_partner,
      metadata: {
        ...currentMetadata,
        ...incomingMetadata,
        workspace_stats: {
          ...asObject(currentMetadata.workspace_stats),
          ...asObject(incomingMetadata.workspace_stats),
        },
        provider_access: {
          ...asObject(currentMetadata.provider_access),
          ...asObject(incomingMetadata.provider_access),
        },
      },
    });
  });

  return [...merged.values()];
};

export const filterWorkspaceCards = (workspaces, { tab = 'active', search = '' } = {}) => {
  const searchTerm = String(search || '').trim().toLowerCase();

  return workspaces.filter((workspace) => {
    const matchesTab = tab === 'archived'
      ? workspace.isArchived
      : tab === 'attention'
        ? !workspace.isArchived && workspace.attentionRequired
        : !workspace.isArchived;
    return matchesTab && (!searchTerm || workspace.searchText.includes(searchTerm));
  });
};

export const workspaceSummary = (workspaces, now = new Date()) => {
  const recentThreshold = 7 * 24 * 60 * 60 * 1000;
  const nowTime = now.getTime();

  return {
    active: workspaces.filter((workspace) => workspace.lifecycle === 'active').length,
    attention: workspaces.filter((workspace) => !workspace.isArchived && workspace.attentionRequired).length,
    execution: workspaces.filter((workspace) => !workspace.isArchived && workspace.isInExecution).length,
    recentlyUpdated: workspaces.filter((workspace) => {
      if (!workspace.updatedAt) return false;
      const timestamp = new Date(workspace.updatedAt).getTime();
      const age = nowTime - timestamp;
      return Number.isFinite(timestamp) && age >= 0 && age <= recentThreshold;
    }).length,
  };
};

export const formatWorkspaceDate = (value) => {
  if (!value) return 'Not available';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Not available';
  return date.toLocaleString([], {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

export const formatRelativeTime = (value, now = new Date()) => {
  if (!value) return 'Update time unavailable';
  const timestamp = new Date(value).getTime();
  if (!Number.isFinite(timestamp)) return 'Update time unavailable';

  const seconds = Math.max(0, Math.round((now.getTime() - timestamp) / 1000));
  if (seconds < 60) return 'Updated just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `Updated ${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `Updated ${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `Updated ${days}d ago`;
  return `Updated ${formatWorkspaceDate(value)}`;
};
